// src/context/AuthContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/firebaseConfig';

export const AuthContext = createContext();

// User types enum
export const USER_TYPES = {
  CUSTOMER: 'customer',
  DELIVERY: 'delivery', 
  ADMIN: 'admin'
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      
      if (currentUser) {
        try {
          // Fetch user profile from Firestore
          const userDocRef = doc(db, 'users', currentUser.uid);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUserProfile(userData);
            
            // Migrate old isAdmin field to userType if needed
            if (userData.isAdmin !== undefined && !userData.userType) {
              const newUserType = userData.isAdmin ? USER_TYPES.ADMIN : USER_TYPES.CUSTOMER;
              await updateDoc(userDocRef, { 
                userType: newUserType,
                isAdmin: null // Remove old field
              });
              setUserProfile(prev => ({ ...prev, userType: newUserType }));
            }
          } else {
            // Default user type is customer
            setUserProfile({ userType: USER_TYPES.CUSTOMER });
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
          setUserProfile({ userType: USER_TYPES.CUSTOMER });
        }
      } else {
        setUserProfile(null);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const logout = () => signOut(auth);

  /**
   * Check if user has specific user type
   * @param {string} userType - User type to check
   * @returns {boolean} - Whether user has the specified type
   */
  const hasUserType = (userType) => {
    return userProfile?.userType === userType;
  };

  /**
   * Check if user is admin
   * @returns {boolean} - Whether user is admin
   */
  const isAdmin = () => {
    return hasUserType(USER_TYPES.ADMIN);
  };

  /**
   * Check if user is delivery person
   * @returns {boolean} - Whether user is delivery person
   */
  const isDeliveryPerson = () => {
    return hasUserType(USER_TYPES.DELIVERY);
  };

  /**
   * Check if user is customer
   * @returns {boolean} - Whether user is customer
   */
  const isCustomer = () => {
    return hasUserType(USER_TYPES.CUSTOMER);
  };

  /**
   * Update user type (admin only)
   * @param {string} userId - User ID to update
   * @param {string} newUserType - New user type
   * @returns {Promise<boolean>} - Success status
   */
  const updateUserType = async (userId, newUserType) => {
    if (!isAdmin()) {
      throw new Error('Only admins can update user types');
    }

    if (!Object.values(USER_TYPES).includes(newUserType)) {
      throw new Error('Invalid user type');
    }

    try {
      const userDocRef = doc(db, 'users', userId);
      await updateDoc(userDocRef, { 
        userType: newUserType,
        updatedAt: new Date().toISOString(),
        updatedBy: user.uid
      });
      
      // If updating current user, update local state
      if (userId === user.uid) {
        setUserProfile(prev => ({ ...prev, userType: newUserType }));
      }
      
      return true;
    } catch (error) {
      console.error('Error updating user type:', error);
      throw error;
    }
  };

  /**
   * Refresh user profile from Firestore
   * Useful after updating user data
   * @returns {Promise<Object>} - Updated user profile
   */
  const refreshUserProfile = async () => {
    if (!user) {
      console.log('❌ No user logged in, cannot refresh profile');
      return null;
    }

    try {
      setLoading(true);
      console.log('🔄 Refreshing user profile from Firestore...');
      
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUserProfile(userData);
        console.log('✅ User profile refreshed successfully');
        console.log('📍 Updated profile data:', userData);
        return userData;
      } else {
        console.log('❌ User document not found in Firestore');
        return null;
      }
    } catch (error) {
      console.error('❌ Error refreshing user profile:', error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    logout,
    userType: userProfile?.userType || USER_TYPES.CUSTOMER,
    hasUserType,
    isAdmin,
    isDeliveryPerson,
    isCustomer,
    updateUserType,
    refreshUserProfile,
    USER_TYPES
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// ✅ Hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
